import { EInvoiceAddressSchemeType, UpgradeSubscriptionModal } from '@components'

import { Button, ButtonDropdown, ButtonsGroup, IconButton, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DeleteDraftConfirmationModal } from '@features/invoices/elements/DeleteDraftConfirmationModal'
import { DownloadAsXRechnungModal } from '@features/invoices/elements/DownloadAsXRechnungModal'
import { SendAsEInvoiceModal } from '@features/invoices/elements/SendAsEInvoiceModal'
import { SendAsEmailModal } from '@features/invoices/elements/SendAsEmailModal'
import { InvoiceStatus } from '@features/invoices/enums/invoiceStatus'
import { SendAsEmailModalVariant } from '@features/invoices/enums/sendAsEmailModalVariant'
import { useDocumentEInvoiceEligibility } from '@features/invoices/hooks/eInvoices/useDocumentEInvoiceEligibility'
import { useIsSubscriptionEligibleForEInvoicing } from '@features/invoices/hooks/eInvoices/useIsSubscriptionEligibleForEInvoicing'
import { SendAsEInvoiceForm } from '@features/invoices/types/sendAsEInvoiceForm'
import { SendAsEmailForm } from '@features/invoices/types/sendAsEmailForm'
import { XRechnungEInvoicingAddressForm } from '@features/invoices/types/xRechnungEInvoicingAddressForm'
import { getDiscountModeTrackingValue } from '@features/invoices/utils/getDiscountModeTrackingValue'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { isDocumentEligibleForXRechnung } from '@features/invoices/utils/isDocumentEligibleForXRechnung'

import { ModalId } from '../../../../../../../../enums/modalId'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useComponentId } from '../../../../../../../../hooks'
import { useInvoiceEditorActions } from '../../../../contexts/invoiceEditorActionsContext'
import { useInvoiceEditor } from '../../../../contexts/invoiceEditorContext'
import { useInvoiceEditorForm } from '../../../../contexts/invoiceEditorFormContext'
import { useSideActionsModals } from '../../../../contexts/sideActionsModalsContext'
import { InvoiceEditorVariant } from '../../../../enums/invoiceEditorVariant'
import { InvoiceEditorView } from '../../../../enums/invoiceEditorView'
import { useMissingComplianceDetails } from '../../../../hooks/missingComplianceDetails/useMissingComplianceDetails'
import { usePredefinedCustomerValues } from '../../../../hooks/missingComplianceDetails/usePredefinedCustomerValues'
import { useInvoiceEditorDocumentType } from '../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../../../../hooks/useInvoiceEditorVariant'
import { useIsInvoiceEditorElementRestricted } from '../../../../hooks/useIsInvoiceEditorElementRestricted'
import { useSubmitXRechnungEInvoicingAddressForm } from '../../../../hooks/useSubmitXRechnungEInvoicingAddressForm'
import { useUpdateReceiverData } from '../../../../hooks/useUpdateReceiverData'
import { useUpdateReceiverDependentValues } from '../../../../hooks/useUpdateReceiverDependentValues'
import { useUpdateReceiverEInvoicingAddressFieldsAndSubmit } from '../../../../hooks/useUpdateReceiverEInvoicingAddressFieldsAndSubmit'
import { FinalizeActionDropdownItem } from '../../../../types/finalizeActionDropdownItem'
import { isActionModal } from '../../../../utils/sideActions/isActionModal'
import { SendAsEInvoiceIneligibilityReasonsTooltip } from './elements/SendAsEInvoiceIneligibilityReasonsTooltip'

export const InvoiceEditorActions = (): ReactElement => {
  const { t } = useTranslation()
  const { updateReceiverData } = useUpdateReceiverData()
  const { documentType, isCreditNote } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const isQuoteEdit = invoiceEditorVariant === InvoiceEditorVariant.QuoteEdit
  const {
    modalActions,
    openApprovalModal,
    openDeleteInvoiceDraftModal,
    openMissingComplianceDetailsModal,
    setApprovalModalFinalizationAction,
    setMissingDetailsModalFinalizationAction,
    shouldSkipApprovalModal,
    shouldSkipMissingComplianceDetailsModal,
  } = useSideActionsModals()
  const { isAppearanceSettingsDirtyRef, setView, view } = useInvoiceEditor()
  const { open: openLeavePreviewModal } = useModal(ModalId.InvoiceEditorLeavePreviewModal)
  const { discountMode, invoiceEditorForm, trackInvoiceEditor, validate } = useInvoiceEditorForm()
  const {
    updateReceiverEInvoicingAddressFieldsAndSubmit: updateReceiverEInvoicingAddressFieldsAndSubmitSendAsEInvoice,
  } = useUpdateReceiverEInvoicingAddressFieldsAndSubmit()
  const { updateReceiverEInvoicingAddressFieldsAndSubmit: updateReceiverEInvoicingAddressFieldsAndSubmitXRechnung } =
    useSubmitXRechnungEInvoicingAddressForm()

  const {
    deleteSalesDocumentDraft,
    isLoading: isLoadingEditorActions,
    isSavingDraft,
    isSavingQuote,
    isSubmitting,
    isDeletingSalesDocumentDraft,
    saveAsDraft,
    saveQuote,
    submit,
  } = useInvoiceEditorActions()

  const upgradeSubscriptionModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openSubscriptionModal } = useModal(upgradeSubscriptionModalId)
  const { isLoadingSubscriptionDetails, isSubscriptionEligibleForEInvoicing } = useIsSubscriptionEligibleForEInvoicing()
  const { isMissingComplianceDetails, isMissingCustomerEInvoicingAddress } = useMissingComplianceDetails()
  const { predefinedCustomerValues } = usePredefinedCustomerValues()
  const { handleReceiverUpdate } = useUpdateReceiverDependentValues()
  const isSaveAsDraftButtonHidden = useIsInvoiceEditorElementRestricted('saveAsDraftButton', 'hidden')

  const { setValue, watch } = invoiceEditorForm

  const [approvedEstimateId, contact, documentLocale, invoiceId, invoiceNumber, invoiceNumberPrefix] = watch([
    'approvedEstimateId',
    'receiver',
    'language',
    'id',
    'invoiceNumber',
    'invoiceNumberPrefix',
  ])
  const { countryId, id, type } = contact || {}

  const {
    ineligibilityReasons,
    isEligibleForEInvoice,
    isLoading: isLoadingEInvoiceEligibility,
    shouldShowSendAsEInvoiceOption,
  } = useDocumentEInvoiceEligibility({
    contactCountry: contact?.countryId,
    contactType: contact?.type,
    documentStatus: InvoiceStatus.Draft,
    documentType,
    isCustomerPublicEntity: contact?.publicEntityRoutingEnabled,
  })

  const isLoading = isLoadingEditorActions || isLoadingSubscriptionDetails || isLoadingEInvoiceEligibility
  const shouldDisplayUpgradeSubscriptionModal = !isSubscriptionEligibleForEInvoicing
  const shouldDisplayDeleteDraftButton = !isCreditNote && invoiceId !== undefined && !isQuoteEdit
  const shouldDisplaySaveAsDraftButton = !isSaveAsDraftButtonHidden
  const shouldDisplaySendAsEInvoiceOption = shouldShowSendAsEInvoiceOption
  const shouldDisplayDownloadAsXRechnungOption = isDocumentEligibleForXRechnung(documentType, countryId, type)
  const dropdownSize = shouldDisplayDownloadAsXRechnungOption ? 'xl' : 'l'

  const sendAsEInvoiceDropdownItem = useMemo(
    (): NavItem<FinalizeActionDropdownItem> => ({
      id: 'sendAsEInvoice',
      children: t('external_invoices.editor.side_actions.send_as_e_invoice'),
      iconLeft: 'fileCode',
      readonly: !isEligibleForEInvoice,
      rightDecoration: <SendAsEInvoiceIneligibilityReasonsTooltip reasons={ineligibilityReasons} />,
      value: 'sendAsEInvoice',
    }),
    [ineligibilityReasons, isEligibleForEInvoice, t],
  )

  const downloadAsXRechnungDropdownItem = useMemo(
    (): NavItem<FinalizeActionDropdownItem> => ({
      id: 'downloadAsXRechnung',
      children: t('external_invoices.editor.side_actions.download_as_xrechnung'),
      iconLeft: 'download',
      value: 'downloadAsXRechnung',
    }),
    [t],
  )

  const dropdownItems = useMemo(
    (): NavItem<FinalizeActionDropdownItem>[] => [
      {
        id: 'sendAsEmail',
        children: t('external_invoices.editor.side_actions.send_by_email'),
        iconLeft: 'mail',
        value: 'sendAsEmail',
      },
      ...(shouldDisplaySendAsEInvoiceOption ? [sendAsEInvoiceDropdownItem] : []),
      ...(shouldDisplayDownloadAsXRechnungOption ? [downloadAsXRechnungDropdownItem] : []),
      {
        id: 'downloadAsPdf',
        children: t('external_invoices.editor.side_actions.download_as_pdf'),
        iconLeft: 'file',
        value: 'downloadAsPdf',
      },
      {
        id: 'approveAndClose',
        children: t('external_invoices.editor.side_actions.approve_and_close'),
        iconLeft: 'badgeCheck',
        value: 'approveAndClose',
      },
    ],
    [
      downloadAsXRechnungDropdownItem,
      sendAsEInvoiceDropdownItem,
      shouldDisplayDownloadAsXRechnungOption,
      shouldDisplaySendAsEInvoiceOption,
      t,
    ],
  )

  const displayedInvoiceNumber = getInvoiceNumberDisplayedValue(invoiceNumber, invoiceNumberPrefix)

  const finalizeInvoice = useCallback(
    (selectedAction: FinalizeActionDropdownItem) => {
      if (isActionModal(modalActions, selectedAction)) {
        setApprovalModalFinalizationAction(() => submit[selectedAction])
        modalActions[selectedAction]?.()
        return
      }

      submit[selectedAction]()
    },
    [modalActions, setApprovalModalFinalizationAction, submit],
  )

  const handleSelect = useCallback(
    async (_: string, selectedAction: FinalizeActionDropdownItem) => {
      await updateReceiverData()
      const isFormValid = await validate()
      const isSelectedActionSendAsEInvoice = selectedAction === 'sendAsEInvoice'

      trackInvoiceEditor('Finalize action clicked', {
        option: selectedAction,
        discountMode: getDiscountModeTrackingValue(discountMode),
      })

      if (isSelectedActionSendAsEInvoice && shouldDisplayUpgradeSubscriptionModal) {
        openSubscriptionModal()
        return
      }

      if (!isFormValid) {
        if (view === InvoiceEditorView.Preview) {
          if (isAppearanceSettingsDirtyRef.current) {
            openLeavePreviewModal()
          } else {
            setView(InvoiceEditorView.Editor)
          }
        }
        return
      }

      // One modal collects missing compliance details for all supported markets:
      // FR uses it for all finalize options, DE/NL/DK use it only for the "Send as e-invoice" option.
      const shouldDisplayMissingComplianceDetailsModal =
        (isSelectedActionSendAsEInvoice || !shouldSkipMissingComplianceDetailsModal) && isMissingComplianceDetails

      if (shouldDisplayMissingComplianceDetailsModal) {
        setMissingDetailsModalFinalizationAction(() => () => finalizeInvoice(selectedAction))
        openMissingComplianceDetailsModal()
        return
      }

      finalizeInvoice(selectedAction)
    },
    [
      discountMode,
      finalizeInvoice,
      isAppearanceSettingsDirtyRef,
      isMissingComplianceDetails,
      openLeavePreviewModal,
      openMissingComplianceDetailsModal,
      openSubscriptionModal,
      setMissingDetailsModalFinalizationAction,
      setView,
      shouldDisplayUpgradeSubscriptionModal,
      shouldSkipMissingComplianceDetailsModal,
      trackInvoiceEditor,
      updateReceiverData,
      validate,
      view,
    ],
  )

  const handleFinalizeButtonDropdownClick = useCallback(() => {
    trackInvoiceEditor('Finalize button dropdown clicked')
  }, [trackInvoiceEditor])

  const handleDeleteSalesDocumentDraftConfirm = useCallback(() => {
    if (invoiceId) {
      deleteSalesDocumentDraft({ approvedEstimateId, documentId: invoiceId, documentType })
    }
  }, [invoiceId, deleteSalesDocumentDraft, approvedEstimateId, documentType])

  const handleSaveAsDraftButtonClick = useCallback(async () => {
    trackInvoiceEditor('Save as Draft clicked')
    const isFormValid = await validate()

    if (!isFormValid) {
      if (view === InvoiceEditorView.Preview) {
        if (isAppearanceSettingsDirtyRef.current) {
          openLeavePreviewModal()
        } else {
          setView(InvoiceEditorView.Editor)
        }
      }
      return
    }

    saveAsDraft()
  }, [isAppearanceSettingsDirtyRef, openLeavePreviewModal, saveAsDraft, setView, trackInvoiceEditor, validate, view])

  const handleSaveQuoteButtonClick = useCallback(async () => {
    await updateReceiverData()
    const isFormValid = await validate()

    if (!isFormValid) {
      if (view === InvoiceEditorView.Preview) {
        if (isAppearanceSettingsDirtyRef.current) {
          openLeavePreviewModal()
        } else {
          setView(InvoiceEditorView.Editor)
        }
      }
      return
    }

    saveQuote()
  }, [isAppearanceSettingsDirtyRef, openLeavePreviewModal, saveQuote, setView, updateReceiverData, validate, view])

  const handleSendAsEmailFormSubmit = useCallback(
    (formData: SendAsEmailForm) => {
      if (shouldSkipApprovalModal) {
        submit.sendAsEmail({
          sendAsEmailFormData: formData,
        })
        return
      }

      setApprovalModalFinalizationAction(
        () => () =>
          submit.sendAsEmail({
            sendAsEmailFormData: formData,
          }),
      )
      openApprovalModal()
    },
    [openApprovalModal, setApprovalModalFinalizationAction, shouldSkipApprovalModal, submit],
  )

  const handleDownloadAsXRechnungSubmit = useCallback(
    (xRechnungFormData: XRechnungEInvoicingAddressForm) => {
      if (shouldSkipApprovalModal) {
        updateReceiverEInvoicingAddressFieldsAndSubmitXRechnung(xRechnungFormData)
        return
      }

      setApprovalModalFinalizationAction(
        () => () => updateReceiverEInvoicingAddressFieldsAndSubmitXRechnung(xRechnungFormData),
      )
      openApprovalModal()
    },
    [
      openApprovalModal,
      setApprovalModalFinalizationAction,
      shouldSkipApprovalModal,
      updateReceiverEInvoicingAddressFieldsAndSubmitXRechnung,
    ],
  )

  const handleSendAsEInvoiceFormSubmit = useCallback(
    (formData: SendAsEInvoiceForm) => {
      if (shouldSkipApprovalModal) {
        updateReceiverEInvoicingAddressFieldsAndSubmitSendAsEInvoice({ formData })
        return
      }

      setApprovalModalFinalizationAction(
        () => () => updateReceiverEInvoicingAddressFieldsAndSubmitSendAsEInvoice({ formData }),
      )
      openApprovalModal()
    },
    [
      openApprovalModal,
      setApprovalModalFinalizationAction,
      shouldSkipApprovalModal,
      updateReceiverEInvoicingAddressFieldsAndSubmitSendAsEInvoice,
    ],
  )

  const handleBeforeSendAsEInvoiceSubmit = useCallback(() => {
    const { ean, eInvoiceScheme, publicEntityRoutingEnabled } = contact || {}

    if (eInvoiceScheme === EInvoiceAddressSchemeType.LWID && publicEntityRoutingEnabled) {
      setValue('buyerReference', ean)
    }
  }, [contact, setValue])

  return (
    <>
      <ButtonsGroup wrap={false}>
        {shouldDisplayDeleteDraftButton && (
          <IconButton
            disabled={isLoading || isSavingDraft || isSubmitting}
            icon="trash"
            loading={isDeletingSalesDocumentDraft}
            onClick={openDeleteInvoiceDraftModal}
            variant="outlined"
          />
        )}
        {shouldDisplaySaveAsDraftButton && (
          <Button
            disabled={isLoading || isSubmitting}
            loading={isSavingDraft}
            onClick={handleSaveAsDraftButtonClick}
            variant="outlined"
          >
            {t('external_invoices.editor.side_actions.save_draft')}
          </Button>
        )}
        {isQuoteEdit ? (
          <Button disabled={isLoading} loading={isSavingQuote} onClick={handleSaveQuoteButtonClick}>
            {t('external_invoices.editor.side_actions.save')}
          </Button>
        ) : (
          <ButtonDropdown
            disabled={isLoading || isSavingDraft}
            dropdownSize={dropdownSize}
            items={dropdownItems}
            loading={isSubmitting}
            onClick={handleFinalizeButtonDropdownClick}
            onSelect={handleSelect}
          >
            {t('external_invoices.editor.side_actions.finalize')}
          </ButtonDropdown>
        )}
      </ButtonsGroup>
      <DeleteDraftConfirmationModal
        documentType={documentType}
        id={ModalId.DeleteInvoiceDraftModal}
        isDeleting={isDeletingSalesDocumentDraft}
        onOk={handleDeleteSalesDocumentDraftConfirm}
      />
      <SendAsEmailModal
        contactId={id}
        documentLocale={documentLocale}
        documentType={documentType}
        id={ModalId.InvoiceEditorSendAsEmailModal}
        invoiceNumber={displayedInvoiceNumber}
        isSubmitting={isSubmitting}
        onSubmit={handleSendAsEmailFormSubmit}
        variant={SendAsEmailModalVariant.Invoice}
        view="invoiceEditor"
      />
      <DownloadAsXRechnungModal
        customer={contact}
        id={ModalId.InvoiceEditorDownloadAsXRechnungModal}
        isSubmitting={isSubmitting}
        onSubmit={handleDownloadAsXRechnungSubmit}
      />
      <SendAsEInvoiceModal
        id={ModalId.InvoiceEditorSendAsEInvoiceModal}
        isMissingCustomerEInvoicingAddress={isMissingCustomerEInvoicingAddress}
        isSubmitting={isSubmitting}
        onBeforeSubmit={handleBeforeSendAsEInvoiceSubmit}
        onCustomerUpdated={handleReceiverUpdate}
        onSubmit={handleSendAsEInvoiceFormSubmit}
        predefinedCustomerValues={predefinedCustomerValues}
        view="invoiceEditor"
        size="s"
      />
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        shouldRedirectToZervantPlanPage
        trackingContext={TrackingContext.ShineEInvoicing}
      />
    </>
  )
}
